/**
 * `/auth/callback` — second leg of the Hosted-UI login. Exchanges `code` + `state` (via the SDK),
 * then returns the user to where they started.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Skeleton } from '@/components/ui/StateMessage'

import { AuthShell } from './AuthShell'
import styles from './AuthShell.module.css'
import { beginLogin, completeLoginFromCallback, consumeReturnPath } from './identityAuth'

export function AuthCallbackScreen() {
  const navigate = useNavigate()
  const started = useRef(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (started.current) return
    started.current = true
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    if (params.get('error') || !code) {
      setError(params.get('error_description') ?? 'The sign-in was cancelled or did not return a code.')
      return
    }
    completeLoginFromCallback(code, params.get('state'))
      .then(() => navigate(consumeReturnPath(), { replace: true }))
      .catch(() => setError('We could not complete the sign-in. It may have expired — please try again.'))
  }, [navigate])

  const handleRetry = useCallback(() => {
    void beginLogin()
  }, [])

  if (!error) {
    return (
      <AuthShell icon="lock" title="Signing you in…">
        <Skeleton label="Completing sign-in" />
      </AuthShell>
    )
  }
  return (
    <AuthShell icon="alert" tone="bad" title="Sign-in failed">
      <p className={styles.text}>{error}</p>
      <div className={styles.actions}>
        <button type="button" className={styles.primary} onClick={handleRetry}>
          Try again
        </button>
      </div>
    </AuthShell>
  )
}
